import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, RefreshCw, Mail, X, CheckCheck, Undo2, MailOpen, Download } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RowSizeControl } from "@/components/documents/useDocSizes";
import DocumentFilters from "@/components/documents/DocumentFilters";
import DocumentTable from "@/components/documents/DocumentTable";
import UploadDocumentDialog from "@/components/documents/UploadDocumentDialog";
import SendEmailDialog from "@/components/documents/SendEmailDialog";
import MarkDoneDialog from "@/components/documents/MarkDoneDialog";
import { BRAND } from "@/lib/constants";
import { portalDisplayName } from "@/lib/portal_display";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 50;

export default function DocumentsPage() {
  const { user } = useAuth();

  // Publish the sticky filters' height so the table header pins right under them.
  // 0 when the block is not sticky (< lg).
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("docs-filters-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleFilters = () =>
    setFiltersCollapsed((c) => {
      try {
        window.localStorage.setItem("docs-filters-collapsed", c ? "0" : "1");
      } catch {
        /* storage unavailable */
      }
      return !c;
    });
  const pageRef = useRef(null);
  const filtersRef = useRef(null);
  useEffect(() => {
    const root = pageRef.current;
    const el = filtersRef.current;
    if (!root || !el) return undefined;
    const set = () => {
      const sticky = window.getComputedStyle(el).position === "sticky";
      root.style.setProperty("--docs-filters-h", sticky ? `${el.offsetHeight}px` : "0px");
    };
    set();
    window.addEventListener("resize", set);
    if (typeof ResizeObserver === "undefined") {
      return () => window.removeEventListener("resize", set);
    }
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", set);
    };
  }, []);

  // URL search params — doc_type is multi-value (repeated ?doc_type=A&doc_type=B)
  const [searchParams, setSearchParams] = useSearchParams();

  // Read selected doc types from URL (repeated params).
  // Memoized so the array reference is stable across renders — without this,
  // useCallback(fetchDocuments) re-creates every render → useEffect re-fires
  // → setState → render → infinite loop (React error #185).
  const selectedDocTypes = useMemo(
    () => searchParams.getAll("doc_type"),
    [searchParams]
  );

  // Stats — still fetched because per_portal + member_names feed the filter dropdowns
  const [stats, setStats] = useState(null);

  // doc_type_groups from stats endpoint (canonical ordered groups)
  const [docTypeGroups, setDocTypeGroups] = useState(null);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Filters, sort and page live in the URL so Back from the doc viewer restores
  // them. Memoized: a fresh object each render would re-fire fetchDocuments.
  const filters = useMemo(
    () => ({
      portal: searchParams.get("portal") || "",
      memberName: searchParams.get("member") || "",
      groupId: searchParams.get("group") || "",
      dateFrom: searchParams.get("from") || "",
      dateTo: searchParams.get("to") || "",
      search: searchParams.get("q") || "",
    }),
    [searchParams]
  );
  const sortKey = searchParams.get("sort") || "date";
  const sortDir = searchParams.get("order") || "desc";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  // Every handler must write all its changes in ONE call: functional
  // setSearchParams calls don't chain within a tick. replace keeps Back from the
  // viewer to a single press.
  const setParams = useCallback(
    (kv) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          Object.entries(kv).forEach(([k, v]) => {
            if (k === "doc_type") {
              p.delete("doc_type");
              v.forEach((t) => p.append("doc_type", t));
            } else if (v === "" || v == null) {
              p.delete(k);
            } else {
              p.set(k, String(v));
            }
          });
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const setPage = (n) => setParams({ page: n > 1 ? n : "" });

  // Groups for filter dropdown
  const [groups, setGroups] = useState([]);

  // Canonical members from member_aliases.yaml — drives the member-name
  // dropdown so user-edited overrides map to the canonical taxonomy.
  const [canonicalMembers, setCanonicalMembers] = useState([]);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchEmailOpen, setBatchEmailOpen] = useState(false);

  // "Select all matching" state — true when the user has selected all docs
  // across all pages (not just the current page).
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [doneDialog, setDoneDialog] = useState(null);

  // Fetch stats (powers the filter dropdowns). Refetchable so a manual upload
  // or a metadata edit surfaces new member/type/portal values in the filters
  // without a page reload. The "+N new docs since last visit" toast only fires
  // on the initial mount (toastNew); tenant-scoped localStorage key so a shared
  // browser doesn't leak counts across logins.
  const fetchStats = useCallback(async ({ toastNew = false } = {}) => {
    try {
      const r = await api.get("/documents/stats");
      setStats(r.data);
      if (Array.isArray(r.data?.doc_type_groups)) {
        setDocTypeGroups(r.data.doc_type_groups);
      }
      if (toastNew) {
        const tenantKey = user?.tenant_id || "anon";
        const storageKey = `elementar.docs.lastSeenCount.${tenantKey}`;
        const prev = parseInt(localStorage.getItem(storageKey) || "0", 10);
        const curr = r.data?.total_documents || 0;
        const delta = curr - prev;
        if (prev > 0 && delta > 0) {
          toast.info(
            `${delta} new document${delta === 1 ? "" : "s"} since your last visit`,
            { duration: 6000 }
          );
        }
        localStorage.setItem(storageKey, String(curr));
      }
    } catch {
      /* non-fatal — dropdowns just stay as-is */
    }
  }, [user?.tenant_id]);

  // Fetch stats + groups once on mount.
  useEffect(() => {
    fetchStats({ toastNew: true });

    api
      .get("/groups")
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => {});

    api
      .get("/portfolio/v2/canonical-members")
      .then((r) => setCanonicalMembers(r.data?.members || []))
      .catch(() => setCanonicalMembers([]));
  }, [fetchStats]);

  // Handler for updating selected doc types — writes back to URL as repeated params
  const handleDocTypesChange = useCallback(
    (newSelected) => {
      setParams({ doc_type: newSelected, page: "" });
    },
    [setParams]
  );

  // Fetch documents when filters/sort/page/selectedDocTypes change
  // A `quiet` reconcile refetches server truth WITHOUT the loading flash, the
  // selection reset, or clearing the visible rows — so it doesn't trigger the
  // jarring full re-render an ordinary (loud) fetch causes. Loud is the default
  // (initial load, filter/sort/page change, Refresh button) and is unchanged.
  const fetchDocuments = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
      setError(null);
      // Clear selection on any filter/sort/page change
      setSelectedIds(new Set());
      setSelectAllMatching(false);
    }
    try {
      const params = new URLSearchParams();
      if (filters.portal) params.set("portal", filters.portal);
      if (filters.memberName === "__none__") {
        // "(No Member)" sentinel — backend reads no_member=1
        params.set("no_member", "1");
      } else if (filters.memberName) {
        params.set("member_name", filters.memberName);
      }
      if (filters.dateFrom) params.set("date_from", filters.dateFrom);
      if (filters.dateTo) params.set("date_to", filters.dateTo);
      if (filters.search) params.set("q", filters.search);
      // Encode selected doc types as repeated params (NOT comma-separated)
      if (selectedDocTypes.length > 0) {
        selectedDocTypes.forEach((t) => params.append("doc_type", t));
      }
      if (filters.groupId) params.set("group_id", filters.groupId);
      params.set("page", String(page));
      params.set("per_page", String(PAGE_SIZE));
      params.set("sort", sortKey);
      params.set("order", sortDir);

      const res = await api.get(`/documents?${params}`);
      setDocuments(res.data.documents || []);
      setTotalCount(res.data.total || 0);
    } catch {
      if (!quiet) setError("Failed to load documents");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [filters, page, sortKey, sortDir, selectedDocTypes]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Refresh both the list and the stats (filter dropdowns). Used after a manual
  // upload so a freshly-added doc's portal/member/type appears in the filters.
  const refreshAll = useCallback(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  // Sort handler
  const handleSort = (key) => {
    const dir = sortKey === key && sortDir === "desc" ? "asc" : "desc";
    setParams({ sort: key, order: dir, page: "" });
  };

  // Filter change resets to page 1.
  // If the portal changed and the previously-selected member / group
  // no longer exists for the new portal, clear those filters so the
  // page doesn't filter to zero rows on a stale value.
  const handleFilterChange = (newFilters) => {
    const portalChanged = newFilters.portal !== filters.portal;
    let nextDocTypes = null;
    if (portalChanged && newFilters.portal) {
      const newMembers = stats?.members_by_portal?.[newFilters.portal] || [];
      const newDocTypes = stats?.doc_types_by_portal?.[newFilters.portal] || [];
      if (newFilters.memberName && !newMembers.includes(newFilters.memberName)) {
        newFilters = { ...newFilters, memberName: "" };
      }
      // If any of the URL-selected doc types no longer apply to the new portal,
      // clear them from the URL.
      if (selectedDocTypes.length > 0 && newDocTypes.length > 0) {
        const validTypes = selectedDocTypes.filter((t) => newDocTypes.includes(t));
        if (validTypes.length !== selectedDocTypes.length) {
          nextDocTypes = validTypes;
        }
      }
    }
    // DocumentFilters' debounced search fires on mount with unchanged values;
    // writing then would reset the restored page to 1.
    const unchanged = Object.keys(filters).every((k) => (newFilters[k] || "") === filters[k]);
    if (unchanged && !nextDocTypes) return;
    setParams({
      portal: newFilters.portal,
      member: newFilters.memberName,
      group: newFilters.groupId,
      from: newFilters.dateFrom,
      to: newFilters.dateTo,
      q: newFilters.search,
      page: "",
      ...(nextDocTypes ? { doc_type: nextDocTypes } : {}),
    });
  };

  const activeFilterCount =
    ["portal", "memberName", "groupId", "dateFrom", "dateTo", "search"].filter((k) => filters[k]).length +
    (selectedDocTypes.length > 0 ? 1 : 0);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  // Inline edit handler — calls PATCH and updates local state
  const handleEditField = useCallback(async (blobName, fieldName, value) => {
    try {
      await api.patch(`/documents/${blobName}/metadata`, { [fieldName]: value });
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.blob_name === blobName ? { ...doc, [fieldName]: value } : doc
        )
      );
      // Surface any new member/fund/type value in the filter dropdowns.
      fetchStats();
      toast.success("Updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to update";
      toast.error(msg);
      throw err; // Re-throw so EditableCell reverts
    }
  }, [fetchStats]);

  // Override handler — writes to overrides.fields.<field> namespace so the
  // scraper-derived flat field stays untouched. Used for member_name and
  // doc_type. An empty value clears the override (DELETE).
  // NOTE: blob_name contains slashes (e.g. "alterdomus/2026/04/foo.pdf"). The
  // route is `{doc_id:path}` which requires LITERAL slashes — encoding them as
  // %2F breaks the match. Pass the raw path-style id, same as the legacy
  // /metadata handler.
  const handleOverrideField = useCallback(async (blobName, fieldName, value) => {
    try {
      const path = `/documents/${blobName}/overrides/${fieldName}`;
      if (value === "" || value == null) {
        await api.delete(path);
      } else {
        await api.patch(path, { value });
      }
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.blob_name === blobName ? { ...doc, [fieldName]: value } : doc
        )
      );
      // Surface the edited member/type value in the filter dropdowns. Backed by
      // the override→scraper mirror in patch_override (untagged docs only), so
      // the new value also becomes filterable, not just a display override.
      fetchStats();
      toast.success("Updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to update";
      toast.error(msg);
      throw err;
    }
  }, [fetchStats]);

  // Portal dropdown options: stats-driven, not hardcoded.
  const portalCounts = stats?.per_portal || [];
  const portalOptions = portalCounts
    .map((p) => ({ id: p.portal, name: portalDisplayName(p.portal) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Member + doc-type + group dropdowns narrow to the selected portal when
  // one is picked. The stats endpoint returns per-portal sets so we can do
  // this client-side without re-fetching.
  const selectedPortal = filters.portal || "";
  const allMembers = stats?.member_names || [];
  const membersForPortal = selectedPortal
    ? (stats?.members_by_portal?.[selectedPortal] || [])
    : allMembers;
  const memberOptions = membersForPortal
    .map((m) => ({ id: m, name: m }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Doc Type dropdown — distinct types appearing on docs for the selected
  // portal (or fleet-wide when no portal is picked).
  const allDocTypes = Object.values(stats?.doc_types_by_portal || {})
    .flat();
  const docTypesForPortal = selectedPortal
    ? (stats?.doc_types_by_portal?.[selectedPortal] || [])
    : Array.from(new Set(allDocTypes));
  const docTypeOptions = docTypesForPortal
    .filter(Boolean)
    .map((t) => ({ id: t, name: t }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Group dropdown — narrow to groups whose members include any LP that
  // appears on the selected portal. When no portal is selected, all groups.
  const groupsForPortal = selectedPortal
    ? groups.filter((g) =>
        Array.isArray(g.members) &&
        g.members.some((m) => membersForPortal.includes(m))
      )
    : groups;

  // Build the array of selected doc objects for batch email
  const selectedDocsArray = documents.filter(
    (d) => d.blob_name && selectedIds.has(d.blob_name)
  );

  // True when every visible doc on this page is selected.
  const allVisibleSelected =
    documents.length > 0 && documents.every((d) => selectedIds.has(d.blob_name));

  // Contextual done/undone button: show "Mark as not done" only when every
  // selected doc is already done; otherwise show "Mark as done".
  const allSelectedDone =
    selectedDocsArray.length > 0 && selectedDocsArray.every((d) => d.is_done);

  // Build the same query params used by fetchDocuments so /all-ids gets the
  // same filter context as the current page.
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (filters.portal) params.set("portal", filters.portal);
    if (filters.memberName === "__none__") {
      params.set("no_member", "1");
    } else if (filters.memberName) {
      params.set("member_name", filters.memberName);
    }
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    if (filters.search) params.set("q", filters.search);
    if (selectedDocTypes.length > 0) {
      selectedDocTypes.forEach((t) => params.append("doc_type", t));
    }
    if (filters.groupId) params.set("group_id", filters.groupId);
    return params;
  };

  // Fetch ALL matching ids across every page and select them.
  const handleSelectAllMatching = async () => {
    try {
      const params = buildFilterParams();
      const res = await api.get(`/documents/all-ids?${params}`);
      setSelectedIds(new Set(res.data.blob_names));
      setSelectAllMatching(true);
    } catch {
      toast.error("Failed to select all documents");
    }
  };

  // Post a list of blob paths to a URL in chunks of ≤500.
  const postChunked = async (url, blobPaths, extra = {}) => {
    const CHUNK_SIZE = 500;
    for (let i = 0; i < blobPaths.length; i += CHUNK_SIZE) {
      const chunk = blobPaths.slice(i, i + CHUNK_SIZE);
      await api.post(url, { blob_paths: chunk, ...extra });
    }
  };

  // Bulk "Mark as done" handler
  const handleMarkDone = async (note) => {
    const paths = Array.from(selectedIds);
    if (paths.length === 0) return;
    // Only reconcile when the action spanned pages (select-all-matching); the
    // optimistic update below already covers the visible page.
    const wasSelectAll = selectAllMatching;

    // Optimistic update (visible docs only)
    setDocuments((prev) =>
      prev.map((doc) =>
        paths.includes(doc.blob_name) ? { ...doc, is_done: true, done_note: note, done_at: new Date().toISOString() } : doc
      )
    );
    setSelectedIds(new Set());
    setSelectAllMatching(false);

    try {
      await postChunked("/documents/bulk-done", paths, { note });
      toast.success(`Marked ${paths.length} document${paths.length === 1 ? "" : "s"} as done`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await postChunked("/documents/bulk-done/undo", paths);
              setDocuments((prev) =>
                prev.map((doc) =>
                  paths.includes(doc.blob_name) ? { ...doc, is_done: false, done_note: null, done_by: null, done_at: null } : doc
                )
              );
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      if (wasSelectAll) fetchDocuments({ quiet: true });
    } catch {
      // Revert optimistic update
      setDocuments((prev) =>
        prev.map((doc) =>
          paths.includes(doc.blob_name) ? { ...doc, is_done: false } : doc
        )
      );
      toast.error("Failed to mark as done");
    }
  };

  // Single doc "un-done" — called from the per-row Done toggle in DocumentTable
  const handleUndoneSingle = async (blob) => {
    // Optimistic update
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.blob_name === blob ? { ...doc, is_done: false, done_note: null, done_by: null, done_at: null } : doc
      )
    );
    try {
      await api.patch(`/documents/${encodeURIComponent(blob)}/done`, { is_done: false });
    } catch {
      // Revert
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.blob_name === blob ? { ...doc, is_done: true } : doc
        )
      );
      toast.error("Failed to mark as not done");
    }
  };

  // Single doc "mark done" — called from the per-row Done toggle in DocumentTable
  const handleMarkDoneSingle = async (blob, note) => {
    // Optimistic update
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.blob_name === blob ? { ...doc, is_done: true, done_note: note, done_at: new Date().toISOString() } : doc
      )
    );
    try {
      await api.patch(`/documents/${encodeURIComponent(blob)}/done`, { is_done: true, note });
    } catch {
      // Revert
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.blob_name === blob ? { ...doc, is_done: false } : doc
        )
      );
      toast.error("Failed to mark as done");
    }
  };

  // Confirm step shared by every done control on this page: { mode, blob } (blob null = bulk).
  const confirmDone = (note) => {
    const d = doneDialog;
    setDoneDialog(null);
    if (!d) return;
    if (d.mode === "done") {
      if (d.blob) handleMarkDoneSingle(d.blob, note);
      else handleMarkDone(note);
    } else if (d.blob) {
      handleUndoneSingle(d.blob);
    } else {
      handleMarkNotDone();
    }
  };

  // Bulk "Mark as not done"
  const handleMarkNotDone = async () => {
    const paths = Array.from(selectedIds);
    if (paths.length === 0) return;
    const wasSelectAll = selectAllMatching; // reconcile only when off-page docs changed

    // Optimistic update (visible docs only)
    setDocuments((prev) =>
      prev.map((doc) =>
        paths.includes(doc.blob_name) ? { ...doc, is_done: false } : doc
      )
    );
    setSelectedIds(new Set());
    setSelectAllMatching(false);

    try {
      await postChunked("/documents/bulk-done/undo", paths);
      toast.success(`Marked ${paths.length} document${paths.length === 1 ? "" : "s"} as not done`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await postChunked("/documents/bulk-done", paths);
              setDocuments((prev) =>
                prev.map((doc) =>
                  paths.includes(doc.blob_name) ? { ...doc, is_done: true } : doc
                )
              );
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      if (wasSelectAll) fetchDocuments({ quiet: true });
    } catch {
      // Revert
      setDocuments((prev) =>
        prev.map((doc) =>
          paths.includes(doc.blob_name) ? { ...doc, is_done: true } : doc
        )
      );
      toast.error("Failed to mark as not done");
    }
  };

  // Bulk "Mark as read" — clears is_new
  const handleMarkRead = async () => {
    const paths = Array.from(selectedIds);
    if (paths.length === 0) return;
    const wasSelectAll = selectAllMatching; // reconcile only when off-page docs changed

    // Optimistic update (visible docs only)
    setDocuments((prev) =>
      prev.map((doc) =>
        paths.includes(doc.blob_name) ? { ...doc, is_new: false } : doc
      )
    );
    setSelectedIds(new Set());
    setSelectAllMatching(false);

    try {
      await postChunked("/documents/bulk-read", paths);
      toast.success(`Marked ${paths.length} document${paths.length === 1 ? "" : "s"} as read`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await postChunked("/documents/bulk-read/undo", paths);
              setDocuments((prev) =>
                prev.map((doc) =>
                  paths.includes(doc.blob_name) ? { ...doc, is_new: true } : doc
                )
              );
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      if (wasSelectAll) fetchDocuments({ quiet: true });
    } catch {
      // Revert
      setDocuments((prev) =>
        prev.map((doc) =>
          paths.includes(doc.blob_name) ? { ...doc, is_new: true } : doc
        )
      );
      toast.error("Failed to mark as read");
    }
  };

  // Bulk download — fetch attachment-forced SAS URLs for the selection and
  // trigger one save per file (separate files, no server-side ZIP). Staggered
  // ~300ms apart to avoid the browser throttling rapid programmatic downloads;
  // the browser shows a one-time "download multiple files?" prompt.
  // Non-destructive — the selection is preserved.
  const handleBulkDownload = async () => {
    const paths = Array.from(selectedIds);
    if (paths.length === 0) return;
    if (paths.length > 50) {
      toast.error("Bulk download supports up to 50 files at once — refine the selection or use Send by email");
      return;
    }
    try {
      const res = await api.post("/documents/bulk-download-urls", { blob_paths: paths });
      const urls = res.data?.urls || [];
      if (urls.length === 0) {
        toast.error("No downloadable files in the selection");
        return;
      }
      urls.forEach(({ url, filename }, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = url;
          a.download = filename || "";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }, i * 300);
      });
      toast.success(`Downloading ${urls.length} document${urls.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Bulk download failed");
    }
  };

  return (
    <div ref={pageRef} className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Documents</h1>
        <p className="text-sm text-gray-500 mt-1">
          View and download all scraped documents from your subscribed portals.
        </p>
      </div>

      {/* Filters */}
      <div
        ref={filtersRef}
        data-testid="docs-filters-sticky"
        className="lg:sticky lg:z-[15] bg-gray-50 lg:pb-1 [&_.p-4]:!p-2"
        style={{ top: "var(--app-header-h, 0px)" }}
      >
      <div className="relative pr-[96px]">
        {filtersCollapsed && (
          <div className="flex items-center rounded-lg border bg-white px-3 h-7 text-xs text-gray-600">
            <span data-testid="filters-collapsed-summary">
              Filters{activeFilterCount > 0 ? ` · ${activeFilterCount} active` : ""}
            </span>
          </div>
        )}
        <div hidden={filtersCollapsed}>
        <DocumentFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          portals={portalOptions}
          members={memberOptions}
          docTypes={docTypeOptions}
          docTypeGroups={docTypeGroups}
          groups={[...groupsForPortal].sort((a, b) => a.name.localeCompare(b.name))}
          selectedDocTypes={selectedDocTypes}
          onDocTypesChange={handleDocTypesChange}
        />
        </div>
        {/* Fixed-width slot, same position expanded or collapsed: row size -/+ and the toggle */}
        <div className="absolute right-0 top-0 flex w-[92px] items-center justify-end gap-1">
          <RowSizeControl />
          <button
            type="button"
            aria-label={filtersCollapsed ? "Show filters" : "Hide filters"}
            aria-expanded={!filtersCollapsed}
            onClick={toggleFilters}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
          >
            {filtersCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>
      </div>

      {/* Select-all-matching banner */}
      {allVisibleSelected && !selectAllMatching && totalCount > documents.length && (
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
          style={{ backgroundColor: BRAND.soft, borderColor: BRAND.text + "33" }}
          data-testid="select-all-matching-banner"
        >
          <span style={{ color: BRAND.text }}>
            All {documents.length} on this page are selected.
          </span>
          <button
            className="underline font-medium"
            style={{ color: BRAND.text }}
            onClick={handleSelectAllMatching}
            data-testid="select-all-matching-btn"
          >
            Select all {totalCount} documents
          </button>
        </div>
      )}
      {selectAllMatching && (
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
          style={{ backgroundColor: BRAND.soft, borderColor: BRAND.text + "33" }}
          data-testid="select-all-matching-active-banner"
        >
          <span style={{ color: BRAND.text }}>
            All {totalCount} documents are selected.
          </span>
          <button
            className="underline font-medium"
            style={{ color: BRAND.text }}
            onClick={() => { setSelectedIds(new Set()); setSelectAllMatching(false); }}
            data-testid="select-all-matching-clear-btn"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Documents table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <CardTitle className="text-sm">All Documents</CardTitle>
              <CardDescription className="text-xs">
                {loading ? "Loading..." : `${totalCount} documents`}
              </CardDescription>
            </div>
            {/* Bulk action bar — opens inline next to the title when >= 1 doc is
                selected, so checking a row no longer pushes the whole table down. */}
            {selectedIds.size > 0 && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm flex-wrap"
                style={{ backgroundColor: BRAND.soft, borderColor: BRAND.text + "33" }}
                data-testid="bulk-action-bar"
              >
                <span className="font-medium" style={{ color: BRAND.text }}>
                  {selectedIds.size} selected
                </span>
                <Button
                  size="sm"
                  className="h-7 text-xs gap-1 text-white"
                  style={{ backgroundColor: BRAND.bg }}
                  onClick={() => setBatchEmailOpen(true)}
                  data-testid="bulk-send-email-btn"
                >
                  <Mail className="w-3 h-3" />
                  Send by email
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs gap-1 text-white"
                  style={{ backgroundColor: BRAND.bg }}
                  onClick={handleBulkDownload}
                  data-testid="bulk-download-btn"
                >
                  <Download className="w-3 h-3" />
                  Bulk download
                </Button>
                {allSelectedDone ? (
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1 text-white"
                    style={{ backgroundColor: BRAND.bg }}
                    onClick={() => setDoneDialog({ mode: "undone", blob: null })}
                    data-testid="bulk-mark-not-done-btn"
                  >
                    <Undo2 className="w-3 h-3" />
                    Mark as not done
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1 text-white"
                    style={{ backgroundColor: BRAND.bg }}
                    onClick={() => setDoneDialog({ mode: "done", blob: null })}
                    data-testid="bulk-mark-done-btn"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark as done
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-7 text-xs gap-1 text-white"
                  style={{ backgroundColor: BRAND.bg }}
                  onClick={handleMarkRead}
                  data-testid="bulk-mark-read-btn"
                >
                  <MailOpen className="w-3 h-3" />
                  Mark as read
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs gap-1"
                  style={{ color: BRAND.text }}
                  onClick={() => { setSelectedIds(new Set()); setSelectAllMatching(false); }}
                  data-testid="bulk-clear-btn"
                >
                  <X className="w-3 h-3" />
                  Clear selection
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <UploadDocumentDialog onUploaded={refreshAll} />
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAll}
              className="h-7 text-xs gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {error ? (
            <div className="p-12 text-center">
              <p className="text-sm" style={{ color: BRAND.text }}>
                {error}
              </p>
              <button
                onClick={fetchDocuments}
                className="mt-3 text-xs underline"
                style={{ color: BRAND.text }}
              >
                Retry
              </button>
            </div>
          ) : (
            <DocumentTable
              documents={documents}
              sortBy={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              loading={loading}
              onEditField={handleEditField}
              onOverrideField={handleOverrideField}
              canonicalMembers={canonicalMembers}
              fallbackMemberNames={allMembers}
              onOpenDoc={(doc) =>
                setDocuments((prev) =>
                  prev.map((d) => (d.blob_name === doc.blob_name ? { ...d, is_new: false } : d))
                )
              }
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onUndone={(blob) => setDoneDialog({ mode: "undone", blob })}
              onMarkDone={(blob) => setDoneDialog({ mode: "done", blob })}
            />
          )}
          <MarkDoneDialog
            open={!!doneDialog}
            mode={doneDialog?.mode}
            count={doneDialog?.blob ? 1 : selectedIds.size}
            onConfirm={confirmDone}
            onCancel={() => setDoneDialog(null)}
          />
        </CardContent>

        {/* Pagination */}
        {!error && !loading && documents.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[10px] text-gray-400">
              Showing {(page - 1) * PAGE_SIZE + 1}
              &ndash;
              {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="h-7 text-[10px]"
              >
                Prev
              </Button>
              <span className="text-[10px] text-gray-500">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="h-7 text-[10px]"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Batch email dialog */}
      <SendEmailDialog
        open={batchEmailOpen}
        onOpenChange={setBatchEmailOpen}
        docs={selectedDocsArray}
        onSent={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
